import { type FC, type MutableRefObject, useEffect, useMemo, useState } from 'react'

import { Skeleton } from 'antd'

import { Label } from 'common/components/Label/Label'
import { Show } from 'common/components/Show/Show'

import { useNotification } from 'app/providers'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'

import {
  useCreatePatientMedicalIssueMutation,
  useFetchAppointmentMedicalIssuesQuery,
  useFetchPatientMedicalIssuesQuery,
} from 'features/Schedule/api/medicalIssues.api'
import {
  getIssueErrorMessage,
  getNotFoundMessage,
} from 'features/Schedule/api/medicalIssues.transform'
import type { MedicalIssue, MedicalIssueType } from 'features/Schedule/api/medicalIssues.types'

import { InlineCreateIssue } from './InlineCreateIssue'
import {
  type AppointmentIssuesSelection,
  EMPTY_ISSUES_SELECTION,
  type PickerEntry,
  type PickerKey,
} from './issuePicker.types'
import {
  PICKER_TEXT,
  addPickerEntry,
  buildReplaceSetBody,
  problemEntry,
  removePickerEntry,
  seedChosenEntries,
} from './issuePicker.utils'
import styles from './medicalissues.module.scss'
import { ProblemListPicker } from './ProblemListPicker'

const NOTIFICATION_TITLE = 'Medical issues'
const CREATE_FAILED = 'Could not create the problem. Please try again.'

export type MedicalIssuesFieldProps = {
  mode: 'create' | 'edit'
  siteId: string
  patientId?: number | null
  appointmentId?: number | null
  // The full set the save flow sends; read at save time.
  selectionRef: MutableRefObject<AppointmentIssuesSelection>
  disabled?: boolean
}

const toSelection = (chosen: PickerEntry[], dirty: boolean): AppointmentIssuesSelection => {
  const { issue_ids, procedure_code_ids } = buildReplaceSetBody(chosen)
  return { issueIds: issue_ids, procedureCodeIds: procedure_code_ids, dirty }
}

export const MedicalIssuesField: FC<MedicalIssuesFieldProps> = ({
  mode,
  siteId,
  patientId,
  appointmentId,
  selectionRef,
  disabled = false,
}) => {
  const { setNotification } = useNotification()
  const isEdit = mode === 'edit'
  const hasPatient = !!patientId

  const patientIssuesQuery = useFetchPatientMedicalIssuesQuery(
    { patientId: patientId ?? 0 },
    { skip: !hasPatient || isEdit },
  )
  const appointmentIssuesQuery = useFetchAppointmentMedicalIssuesQuery(
    { appointmentId: appointmentId ?? 0, siteId },
    { skip: !isEdit || !appointmentId || !siteId },
  )
  const [createIssue, { isLoading: isCreating }] = useCreatePatientMedicalIssueMutation()

  const [chosen, setChosen] = useState<PickerEntry[]>([])
  const [isDirty, setIsDirty] = useState(false)
  const [createdIssues, setCreatedIssues] = useState<MedicalIssue[]>([])

  // Whatever was chosen belonged to the patient or booking that is no longer the one on screen.
  useEffect(() => {
    setChosen([])
    setIsDirty(false)
    setCreatedIssues([])
  }, [mode, patientId, appointmentId])

  // Edit: seed from what the booking already says it is for, unless the operator is mid-change.
  const appointmentSet = appointmentIssuesQuery.data
  useEffect(() => {
    if (!isEdit || !appointmentSet || isDirty) return
    setChosen(seedChosenEntries(appointmentSet))
  }, [isEdit, appointmentSet, isDirty])

  useEffect(() => {
    selectionRef.current =
      chosen.length || isDirty ? toSelection(chosen, isDirty) : EMPTY_ISSUES_SELECTION
  }, [chosen, isDirty, selectionRef])

  const fetchedIssues = isEdit ? appointmentSet?.issues : patientIssuesQuery.data
  const patientIssues = useMemo(() => {
    const baseIssues = fetchedIssues ?? []
    const knownIds = new Set(baseIssues.map((issue) => issue.id))
    return [...createdIssues.filter((issue) => !knownIds.has(issue.id)), ...baseIssues]
  }, [fetchedIssues, createdIssues])

  const queryError = isEdit
    ? appointmentIssuesQuery.error
    : hasPatient
    ? patientIssuesQuery.error
    : undefined
  const notFoundMessage = getNotFoundMessage(queryError)
  const hasBlockingError = !!queryError && !notFoundMessage

  useEffect(() => {
    if (!hasBlockingError) return
    setNotification({
      title: NOTIFICATION_TITLE,
      description: PICKER_TEXT.loadFailed,
      type: ALERT_CONSTANTS.ERROR,
    })
  }, [hasBlockingError, setNotification])

  const handlePick = (entry: PickerEntry) => {
    setChosen((prev) => addPickerEntry(prev, entry))
    setIsDirty(true)
  }

  const handleRemove = (key: PickerKey) => {
    setChosen((prev) => removePickerEntry(prev, key))
    setIsDirty(true)
  }

  // A newly named problem goes straight onto the visit, as in OpenEMR.
  const handleInlineCreate = async (title: string, type: MedicalIssueType): Promise<boolean> => {
    if (!patientId) return false
    try {
      const created = await createIssue({ patientId, data: { title, type } }).unwrap()
      if (created) {
        setCreatedIssues((prev) => [created, ...prev])
        handlePick(problemEntry(created))
      }
      return true
    } catch (error) {
      setNotification({
        title: NOTIFICATION_TITLE,
        description: getIssueErrorMessage(error, CREATE_FAILED),
        type: ALERT_CONSTANTS.ERROR,
      })
      return false
    }
  }

  if (hasBlockingError) return null

  if (notFoundMessage) {
    return (
      <div className={styles.parent}>
        <Label label={PICKER_TEXT.label} />
        <p className={styles.hint}>{notFoundMessage}</p>
      </div>
    )
  }

  const isListLoading = isEdit ? appointmentIssuesQuery.isLoading : patientIssuesQuery.isLoading

  return (
    <div className={styles.parent}>
      <Show
        when={!isListLoading}
        fallback={
          <div className={styles.picker}>
            <Label label={PICKER_TEXT.label} />
            <Skeleton.Input active block className={styles.skeleton} />
          </div>
        }>
        <ProblemListPicker
          siteId={siteId}
          hasPatient={hasPatient}
          patientIssues={patientIssues}
          chosen={chosen}
          disabled={disabled}
          onPick={handlePick}
          onRemove={handleRemove}
        />
      </Show>
      <InlineCreateIssue
        hasPatient={hasPatient}
        disabled={disabled}
        isSubmitting={isCreating}
        onAdd={handleInlineCreate}
      />
    </div>
  )
}
